/**
 * Agent guidance settings section — what the agent knows and the instructions
 * it follows. What the agent may DO on its own lives on the Agent Autonomy
 * page (AI → Agent Autonomy).
 *
 * Sub-sections:
 *   1. "What we’ve learned about you" — editable Role / Focus / Work patterns
 *      sourced from the learning agents (same fields shown in the welcome
 *      onboarding proposal). Auto-saves on blur.
 *   2. "Manual guidance" — free-form text the user provides for the agent
 *      that it can't learn on its own (scheduling links, specific
 *      instructions). Saved with an explicit Save button.
 *   3. Organization guidance — org-wide instructions, editable by admins.
 *   4. Agent memory — rules learned from the user's decisions (flag-gated).
 */

import { useCallback } from 'react';

import { AgentMemoryCard } from '@/components/settings/agent-memory';
import { useOrg } from '@/contexts/OrgContext';
import { useProfile } from '@/hooks/profile/useProfile';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useCanWrite } from '@/hooks/usePermissions';
import { Button } from '@/lib/base/buttons/button';
import { TextArea } from '@/lib/base/forms/textarea/textarea';
import { useViewHealthMarker } from '@/lib/view-health';

import { useUser } from '../../../../allauth_auth';
import { LearnedInsightsCard } from './components/LearnedInsightsCard';
import { OrganizationGuidanceCard } from './components/OrganizationGuidanceCard';

const MANUAL_GUIDANCE_PLACEHOLDER =
  'e.g., My Calendly link is calendly.com/me – always offer it when scheduling.';

export function AgentGuidanceSection() {
  const { currentOrg } = useOrg();
  const user = useUser();
  useViewHealthMarker('settings-section-ready');
  const canWrite = useCanWrite();
  const { formData, agentGuidanceSaving, updateFormData, saveAgentGuidance } =
    useProfile({ orgId: currentOrg?.id, userId: user?.id });
  // Learning can run before the rules are shown — the backend 404s the memory API
  // on the same flag, so this only avoids a doomed request.
  const isMemoryCardEnabled = useFeatureFlag('decision_memory_settings_card');

  const handleAgentGuidanceChange = useCallback(
    (value: string) => {
      updateFormData({ agent_guidance: value });
    },
    [updateFormData]
  );

  const handleSaveAgentGuidance = useCallback(() => {
    saveAgentGuidance().catch(() => {
      // Error handling is managed by useProfile
    });
  }, [saveAgentGuidance]);

  return (
    <div className='space-y-10'>
      {/* Section header */}
      <div>
        <h2 className='text-lg font-semibold text-fg-primary'>
          Agent guidance
        </h2>
        <p className='text-sm text-fg-tertiary mt-0.5'>
          Provide context to help AI agents work better for you.
        </p>
      </div>

      {/* Learned insights — Role / Focus / Work patterns, auto-saved on blur. */}
      <LearnedInsightsCard />

      {/* Manual guidance — explicit Save button. Hidden for viewers. */}
      <div>
        <h3 className='text-base font-semibold text-fg-primary'>
          Manual guidance
        </h3>
        <p className='text-sm text-fg-tertiary mt-0.5 mb-2'>
          Provide information the AI agent can&apos;t learn on its own, such as
          scheduling links or specific instructions.
        </p>
        {canWrite && (
          <>
            <TextArea
              value={formData.agent_guidance}
              onChange={handleAgentGuidanceChange}
              placeholder={MANUAL_GUIDANCE_PLACEHOLDER}
              rows={4}
              isDisabled={agentGuidanceSaving}
            />
            <div className='mt-3 flex justify-end'>
              <Button
                onClick={handleSaveAgentGuidance}
                isDisabled={agentGuidanceSaving}
                isLoading={agentGuidanceSaving}
                color='primary'
                size='sm'
              >
                Save
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Organization guidance — org-wide instructions for every member's agents. */}
      <OrganizationGuidanceCard />

      {/* Agent memory — per-user rules the agent follows across all conversations. */}
      {isMemoryCardEnabled && <AgentMemoryCard />}
    </div>
  );
}
